import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AudioResponse, AudioSettings, AudioSettingsV2, ILanguage } from '../../core/interfaces';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReadingBoundary, SoundStatus, SpeakingJobType, TxtItemType } from '../../core/reading-utils/enums';
import { BoundaryItem, SpeakAtom } from '../../core/reading-utils/interfaces';
import { SettingsService } from '../settings/settings.service';
import { environment } from 'src/environments/environment';
import { createGuid, isSafary } from '../../core/common';
import { LanguageService } from '../lang/language.service';
import { SpeechEngineVersions } from 'src/app/core/constants';
import { currentSpeechEngine } from 'src/app/core/speech-engine';

interface SoundItem {
  id: string;
  batchId: string;
  jobType: SpeakingJobType;
  startTime: number | null;
  pauseTime: number | null;
  waitingNextPause: number;
  speakAtoms: SpeakAtom[];
  bufferItemIdx: number | null;
  isActive: boolean;
  audioPositions: number[] | null;
  itemsPositions: number[] | null;
  audioBuffer: AudioBufferSourceNode | null;
  canceled: boolean;
}

interface AtomsGroup {
  batchId: string;
  items: SpeakAtom[];
  voice: string;
  speed: number;
}

@Injectable({
  providedIn: 'root'
})
export class TtsService {
  private nextSpeakItemTimerId: number | null = null;

  private readonly playingQueue: SoundItem[] = [];
  private readonly waitingNextPause: number = 500;
  private readonly sndStatBhvSubj: BehaviorSubject<SoundStatus> = new BehaviorSubject(SoundStatus.Stopped);
  private readonly readingPosBhvSubj: BehaviorSubject<BoundaryItem | null> = new BehaviorSubject(null);
  private readonly audioCtx: AudioContext = (
    window.hasOwnProperty('AudioContext') ? new AudioContext() : new (window as any).webkitAudioContext()
  );

  private isUnlocked: boolean = false;

  private get currSndItem(): SoundItem | null { return this.playingQueue.length > 0 ? this.playingQueue[0] : null; }

  get readingPosition(): Observable<BoundaryItem | null> { return this.readingPosBhvSubj.asObservable(); }
  get soundStatus(): Observable<SoundStatus> { return this.sndStatBhvSubj.asObservable(); }
  get soundStatusValue(): SoundStatus { return this.sndStatBhvSubj.value; }
  get activeJobType(): SpeakingJobType | null { return this.currSndItem !== null ? this.currSndItem.jobType : null; }

  constructor(private readonly http: HttpClient,
    private settingsService: SettingsService,
    private languageSvc: LanguageService,
  ) {
    this.unlockAudio();
  }

  speak(speakAtoms: SpeakAtom[], jobType: SpeakingJobType, defaultVoiceName: string, speed: number): void {
    const defaultLang = this.languageSvc.getLangByVoice(defaultVoiceName);

    if (!defaultVoiceName || defaultLang === null) {
      console.warn('Unknown voice. Text can not be spoken.');
      return;
    }

    speakAtoms = this.excludePunctuationSurroundedBySpaces(speakAtoms);

    if (speakAtoms.length === 0 || speakAtoms.filter(x => x.itemType !== TxtItemType.Punctuation).length === 0) {
      console.warn('Empty text received');
      return;
    }

    const groups: AtomsGroup[] = this.createGroups(speakAtoms, defaultLang, defaultVoiceName, speed);

    groups.forEach((gr, idx) => this.createSoundItem(gr, jobType, idx < groups.length - 1));

    if (this.sndStatBhvSubj.getValue() === SoundStatus.Stopped) {
      this.setStatus(SoundStatus.WaitingForSound); // indicate waiting for Nephilim server response.
    }
  }

  private createGroups(speakAtoms: SpeakAtom[], defaultLang: ILanguage, defaultVoiceName: string, speed: number) {
    const groups: AtomsGroup[] = [];
    const batchId: string = createGuid();
    let currGroup: AtomsGroup | null = null;

    for (const atom of speakAtoms) {
      atom.lang = atom.lang === null || atom.lang.trim().length === 0 ? defaultLang.code : atom.lang;
      const langChanged = currGroup !== null && atom.lang !== currGroup.items[currGroup.items.length - 1].lang;
      const sentenceChanged = currGroup !== null && atom.sntncIndex !== currGroup.items[currGroup.items.length - 1].sntncIndex;

      if (currGroup !== null && !langChanged && !sentenceChanged) {
        currGroup.items.push(atom);
      } else {
        const lang = this.languageSvc.getDefaultVoiceByLangCode(atom.lang);
        const name = lang ? lang.name : null;
        const voiceName = atom.lang !== defaultLang.code ? name : defaultVoiceName;
        currGroup = { batchId, items: [atom], voice: voiceName, speed };
        groups.push(currGroup);
      }
    }

    return groups;
  }

  private excludePunctuationSurroundedBySpaces(speakAtoms: SpeakAtom[]) {
    const resultArr: SpeakAtom[] = [];
    for (let i = 0; i < speakAtoms.length; i++) {
      const speakAtom = speakAtoms[i];
      if (i === 0 || speakAtom.itemType !== TxtItemType.Punctuation) {
        resultArr.push(speakAtom);
      } else if (i < speakAtoms.length - 1 && speakAtom.symbPos + 1 === speakAtoms[i + 1].symbPos ||
        speakAtoms[i - 1].text.length + speakAtoms[i - 1].symbPos === speakAtom.symbPos) {
        resultArr.push(speakAtom);
      }
    }
    return resultArr;
  }

  private createSoundItem(atomsGroup: AtomsGroup, jobType: SpeakingJobType, addPause: boolean): void {
    const fileredItems = atomsGroup.items.filter(x => x.itemType !== TxtItemType.Punctuation);

    if (fileredItems.length === 0) {
      return;
    }

    const soundItem = {
      id: createGuid(), batchId: atomsGroup.batchId, jobType, speakAtoms: fileredItems, isActive: false, startTime: null,
      pauseTime: null, waitingNextPause: addPause ? this.waitingNextPause : 0, bufferItemIdx: null,
      audioPositions: null, itemsPositions: null, wordsLength: null, audioBuffer: null, canceled: false
    };

    this.playingQueue.push(soundItem);

    if (environment.debugMessages.TextToSpeechService) {
      console.log('put to queue:', atomsGroup);
    }

    this.getBase64Sound(this.getSolidText(atomsGroup.items), atomsGroup.voice, atomsGroup.speed)
      .then(x => this.procSoundDataResponse(soundItem, x))
      .catch(err => this.processError('Can\'t receive sound. Error:', err));
  }

  private getSolidText(items: SpeakAtom[]): string {
    let resultText = '';
    let symbPos = items[0].symbPos;

    items.forEach((itm: SpeakAtom) => {
      const numSpaces = Math.max(0, itm.symbPos - symbPos);
      resultText += ' '.repeat(numSpaces) + itm.text;
      symbPos += itm.text.length + numSpaces;
    });

    // Replace typographic single quotation marks with typewriter quotation mark
    // Temporary implementation
    resultText = this.replaceTypographicSingleQuotes(resultText);

    return resultText;
  }

  private replaceTypographicSingleQuotes(text: string): string {
    return text.replace(/[‘’]/g, '\'');
  }

  private async getBase64Sound(txt: string, voice: string, speed: number): Promise<AudioResponse> {
    const audioSettings = this.createAudioSettings(txt, voice, speed);
    const httpOpts = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'json' as 'json'
    };

    return this.http.post(this.getText2speechUrl(), audioSettings, httpOpts).toPromise() as Promise<AudioResponse>;
  }

  private getText2speechUrl() {
    return currentSpeechEngine() === SpeechEngineVersions.Skidbladnir ? environment.speech.skidbladnirTtsUrl : environment.speech.nephilimTTShUrl;
  }

  private async procSoundDataResponse(soundItem: SoundItem, response: AudioResponse): Promise<void> {
    try {
      if (soundItem.canceled) {
        return;
      }

      if (soundItem.speakAtoms.length === 1 && soundItem.speakAtoms[0].text === '.' && this.currSndItem !== null && this.currSndItem.id === soundItem.id) {
        this.startNextReadingItem(soundItem);
        return;
      }

      // if (soundItem.speakAtoms.length !== response.AudioPos.length) {
      //   console.warn('Number of words parsed on client is not equal to number of audio positions received from server.');
      // }

      soundItem.audioPositions = response.AudioPos;
      soundItem.itemsPositions = response.CharPos;
      soundItem.audioBuffer = await this.createBuffer(response.ByteArray);

      if (this.sndStatBhvSubj.getValue() !== SoundStatus.Paused
        && this.currSndItem !== null
        && this.currSndItem.id === soundItem.id
      ) {
        this.startCurrentBuffer();
      }
    } catch (err) {
      this.processError('Can\'t play received sound. Error:', err);
    }
  }

  private async createBuffer(data: string): Promise<AudioBufferSourceNode> {
    const audioBufferSource = this.audioCtx.createBufferSource();

    return new Promise(resolve => {
      this.audioCtx.decodeAudioData(Uint8Array.from(atob(data), c => c.charCodeAt(0)).buffer, buffer => {
        audioBufferSource.buffer = buffer;
        audioBufferSource.connect(this.audioCtx.destination);

        resolve(audioBufferSource);
      });
    });
  }

  private startNextReadingItem(soundItem: SoundItem) {
    const currentSoundStatus = this.sndStatBhvSubj.getValue();
        const canPlay = currentSoundStatus === SoundStatus.Stopped || currentSoundStatus === SoundStatus.WaitingForSound;
        if (canPlay && this.playingQueue.length > 0) {
          let currentSpeakAtom = soundItem.bufferItemIdx ? soundItem.speakAtoms[soundItem.bufferItemIdx] : null;

          if (!currentSpeakAtom) {
            currentSpeakAtom = soundItem.speakAtoms[soundItem.speakAtoms.length - 1];
          }

          const prevTimelinePos = soundItem.bufferItemIdx === 0
            ? 0
            : (soundItem.audioPositions && soundItem.bufferItemIdx) ? soundItem.audioPositions[soundItem.bufferItemIdx - 1] : 0;

          const msgItem: BoundaryItem = {
            boundaryType: ReadingBoundary.Text,
            text: soundItem.speakAtoms[0].text,
            wordPos: 0,
            wordIdx: currentSpeakAtom.itemIdx,
            duration: 0,
            prevTimelinePos,
            lastInBuffer: true,
            batchFinished: true
          };
          this.readingPosBhvSubj.next(msgItem);

          if(this.currSndItem !== null) {
            this.removeItemFromQueue(this.currSndItem);
          }
        }
  }

  pause(): void {
    if (this.currSndItem === null) {
      console.error('Pause requested, but no items playing.');
      this.stop();
      return;
    }

    if (environment.debugMessages.TextToSpeechService) {
      console.log('pause requested, status is:', this.sndStatBhvSubj.getValue());
    }

    if (this.sndStatBhvSubj.getValue() === SoundStatus.Playing && this.nextSpeakItemTimerId) {
      window.clearTimeout(this.nextSpeakItemTimerId);
      this.setStatus(SoundStatus.Paused);
      this.audioCtx.suspend();

      this.currSndItem.pauseTime = new Date().getTime();
    } else if (this.sndStatBhvSubj.getValue() === SoundStatus.WaitingForSound && this.currSndItem.audioBuffer === null) {
      this.setStatus(SoundStatus.Paused);

      if (environment.debugMessages.TextToSpeechService) {
        console.log('Set pause for waiting item.', 'status:', this.sndStatBhvSubj.getValue(), 'active item:', this.currSndItem);
      }
    }
  }

  resume(): void {
    const item = this.currSndItem;

    if (item === null) {
      return;
    }

    if (this.sndStatBhvSubj.getValue() === SoundStatus.Paused && item !== null) {
      if (item.audioBuffer === null) {
        this.setStatus(SoundStatus.WaitingForSound);

        if (environment.debugMessages.TextToSpeechService) {
          console.log('Resume empty buffer', 'status:', this.sndStatBhvSubj.getValue(), 'active item:', item);
        }
        return;
      }

      if (!item.isActive) {
        this.setStatus(SoundStatus.Stopped);
        this.startCurrentBuffer();

        if (environment.debugMessages.TextToSpeechService) {
          console.log('Start buffer', 'status:', this.sndStatBhvSubj.getValue(), 'active item:', item);
        }
        return;
      }

      // NOTE (TODO) if you have a sentence that has only one word
      // and you pauze it during that word
      // if will fail because bufferItemIdx is 0 and you can not continue reading then
      if (item.bufferItemIdx !== null && item.pauseTime && item.startTime && item.audioPositions) {
        const currInterval = item.bufferItemIdx === 0
          ? item.audioPositions[item.bufferItemIdx]
          : item.audioPositions[item.bufferItemIdx] - item.audioPositions[item.bufferItemIdx - 1];
        const elapsedTime = item.pauseTime - item.startTime;
        const leftTime = currInterval - elapsedTime;

        this.nextSpeakItemTimerId = window.setTimeout(this.sendReadingPositionEvent.bind(this), leftTime, item);
        this.setStatus(SoundStatus.Playing);
        this.audioCtx.resume();

        item.startTime = new Date().getTime();
        item.pauseTime = null;
      }
    }
  }

  stop(): void {
    if (this.currSndItem !== null) {
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      if (this.currSndItem.audioBuffer !== null) {
        try {
          this.currSndItem.audioBuffer.stop();
        } catch (err) {
          console.error('Unable to stop audioBuffer.');
        }
      }
    }

    this.playingQueue.forEach(x => x.canceled = true);
    this.playingQueue.splice(0);
    this.setStatus(SoundStatus.Stopped);
  }

  private processError(msg: string, err: Error): void {
    this.setStatus(SoundStatus.Error);
    console.error(msg, err);
    this.stop();
  }

  private startCurrentBuffer(): void {
    const currentSoundStatus = this.sndStatBhvSubj.getValue();
    const canPlay = currentSoundStatus === SoundStatus.Stopped || currentSoundStatus === SoundStatus.WaitingForSound;

    if (environment.debugMessages.TextToSpeechService) {
      console.log('check if new item can be played.', 'status:', currentSoundStatus, 'active item:', this.currSndItem);
    }

    if (canPlay && this.playingQueue.length > 0 && this.playingQueue[0].audioBuffer !== null) {
      const item = this.playingQueue[0];
      item.isActive = true;
      item.startTime = new Date().getTime();
      item.bufferItemIdx = 0;

      if (item.audioBuffer !== null) {
        item.audioBuffer.start(0);
      }

      this.setStatus(SoundStatus.Playing);
      this.sendReadingPositionEvent(ReadingBoundary.Word);
    }
  }

  private sendReadingPositionEvent(eventType: ReadingBoundary): void {
    const startTime = new Date().getTime();

    if (this.currSndItem === null || this.currSndItem.audioBuffer === null ||
      this.sndStatBhvSubj.getValue() === SoundStatus.Stopped) {
      return;
    }

    if (this.currSndItem.bufferItemIdx !== null && this.currSndItem.audioPositions !== null) {
      const wrdFinishOnTimeline = this.currSndItem.audioPositions[this.currSndItem.bufferItemIdx];

      const prevTimelinePos = this.currSndItem.bufferItemIdx === 0 ?
        0 : this.currSndItem.audioPositions[this.currSndItem.bufferItemIdx - 1];

      const wordDuration = wrdFinishOnTimeline - prevTimelinePos;

      let currentSpeakAtom = this.currSndItem.speakAtoms[this.currSndItem.bufferItemIdx];

      if (!currentSpeakAtom) {
        currentSpeakAtom = this.currSndItem.speakAtoms[this.currSndItem.speakAtoms.length - 1];
      }

      const bufferFinished = this.currSndItem.bufferItemIdx === this.currSndItem.speakAtoms.length - 1;
      const batchFinished = bufferFinished && this.playingQueue.filter(x => {
        if (this.currSndItem && this.currSndItem.batchId) {
          return x.batchId === this.currSndItem.batchId;
        }
      }).length === 1;
      let msgItem: BoundaryItem | null = null;
      if (this.currSndItem && this.currSndItem.bufferItemIdx !== null && this.currSndItem.itemsPositions) {
        msgItem = {
          boundaryType: eventType,
          text: currentSpeakAtom.text,
          wordPos: this.currSndItem.itemsPositions[this.currSndItem.bufferItemIdx],
          wordIdx: currentSpeakAtom.itemIdx,
          duration: wordDuration, prevTimelinePos,
          lastInBuffer: bufferFinished,
          batchFinished
        };
        if (environment.debugMessages.TextToSpeechService) {
          console.log(`%c sending ${msgItem.lastInBuffer ? 'finish' : 'reading'} event:`, `color:${msgItem.lastInBuffer ? 'red' : 'black'}`,
            'text:', '"' + msgItem.text + '"', 'boundary:', msgItem.boundaryType, 'isLast:', msgItem.lastInBuffer);
        }

        this.readingPosBhvSubj.next(msgItem); // sending position event

        if (eventType !== ReadingBoundary.Text) {
          const nxtEventType = msgItem.lastInBuffer ? ReadingBoundary.Text : ReadingBoundary.Word;

          // preaparing next boundary event
          if (this.currSndItem && this.currSndItem.bufferItemIdx !== null) {
            this.currSndItem.bufferItemIdx += msgItem.lastInBuffer ? 0 : 1;
            const elapsedTime = new Date().getTime() - startTime;
            const leftTime = wordDuration - elapsedTime;
            this.nextSpeakItemTimerId = window.setTimeout(this.sendReadingPositionEvent.bind(this), leftTime, nxtEventType);
          }
        } else {
          const pauseDuration = this.currSndItem !== null ? this.currSndItem.waitingNextPause : 0;
          this.removeItemFromQueue(this.currSndItem);
          this.setStatus(batchFinished ? SoundStatus.Stopped : SoundStatus.WaitingForSound);
          setTimeout(() => { this.startCurrentBuffer(); }, pauseDuration);
        }
      }
    }
  }

  private setStatus(newStatus: SoundStatus): void {
    if (this.sndStatBhvSubj.getValue() !== newStatus) {
      this.sndStatBhvSubj.next(newStatus);
    }

    if (environment.debugMessages.TextToSpeechService) {
      console.log('new sound status is:', JSON.stringify(this.sndStatBhvSubj.getValue()));
    }
  }

  private removeItemFromQueue(item: SoundItem): void {
    const itemIdx = this.playingQueue.indexOf(item);
    if (itemIdx >= 0) {
      this.playingQueue.splice(this.playingQueue.indexOf(item), 1);
    }
  }

  private createAudioSettings(text: string, voice: string, speed: number): AudioSettings | AudioSettingsV2 {
    const pause = this.settingsService.settings.wordsReadingPause || 0;

    if (currentSpeechEngine() === SpeechEngineVersions.Skidbladnir) {
      return {
        text,
        audioSettings: {
          bitrate: 320,
          format: 0
        },
        ttsParams: {
          rate: speed,
          volume: 100,
          voice: voice || this.settingsService.settings.savedVoice,
          pause
        }
      }
    }

    return {
      rate: speed, volume: 100, pause,
      bitrate: 320, format: 0, voice: voice || this.settingsService.settings.savedVoice, text
    };
  }

  // Safari issue, audio context is suspended when start playing buffer first time
  // https://artandlogic.com/2019/07/unlocking-the-web-audio-api/
  private unlockAudio(): void {
    if (this.isUnlocked) {
      return;
    }

    if (!isSafary()) {
      this.isUnlocked = true;
      return;
    }

    // Scratch buffer to prevent memory leaks on iOS.
    // See: https://stackoverflow.com/questions/24119684/web-audio-api-memory-leaks-on-mobile-platforms
    const scratchBuffer = this.audioCtx.createBuffer(1, 1, 22050);

    const unlock = () => {
      const source = this.audioCtx.createBufferSource();
      source.buffer = scratchBuffer;
      source.connect(this.audioCtx.destination);

      // Play the empty buffer.
      source.start(0);

      // Calling resume() on a stack initiated by user gesture is
      // what actually unlocks the audio on Chrome >= 55.
      if (typeof this.audioCtx.resume === 'function') {
        this.audioCtx.resume();
      }

      // Once the source has fired the onended event, indicating it did indeed play,
      // we can know that the audio API is now unlocked.
      source.onended = () => {
        source.disconnect(0);

        this.isUnlocked = true;

        document.removeEventListener('touchstart', unlock, true);
        document.removeEventListener('touchend', unlock, true);
        document.removeEventListener('click', unlock, true);
      };
    };

    document.addEventListener('touchstart', unlock, true);
    document.addEventListener('touchend', unlock, true);
    document.addEventListener('click', unlock, true);
  }
}
